import React, { Suspense, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Box, CircularProgress, Snackbar, Alert } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './i18n/config'; // Initialize i18n
import { HealthProvider } from './contexts/HealthContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SessionExpiryWarning from './components/auth/SessionExpiryWarning';
import ReauthDialog from './components/auth/ReauthDialog';
import { RoutedChunkErrorBoundary } from './components/common/ChunkErrorBoundary';
import TenantAccessGuard from './components/auth/TenantAccessGuard';
import PortalAccessGuard from './components/auth/PortalAccessGuard';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { adminTheme, portalTheme } from './config/theme';
import { AzureAuthProvider } from './contexts/AzureAuthContext';
import { getRuntimeConfig } from './runtime/runtimeConfig';

// The two layout trees are the app's biggest split point: admin users never pay
// for the portal pages and vice versa. Each layout lazy-loads its own routed
// pages as further chunks.
const AppLayout = lazyWithRetry(() => import('./components/layout/AppLayout'));
const PortalLayout = lazyWithRetry(() => import('./components/portal/layout/PortalLayout'));

// Themes the chrome (grid lines, axis ticks/labels, legend text) of every
// Recharts chart from the active palette. Charts pass colors as SVG presentation
// attributes / inline styles, which these CSS rules override. We read from
// `theme.vars` (the `var(--mui-...)` references) rather than `theme.palette`, so
// the chrome follows the light/dark color scheme — `theme.palette` under CSS vars
// is frozen to the default scheme. Series colors are handled per-chart.
const rechartsThemeStyles = (theme: Theme) => ({
  '.recharts-cartesian-grid line': { stroke: theme.vars.palette.divider },
  '.recharts-cartesian-axis-line, .recharts-cartesian-axis-tick-line': {
    stroke: theme.vars.palette.divider,
  },
  '.recharts-cartesian-axis-tick-value, .recharts-label, .recharts-pie-label-text': {
    fill: theme.vars.palette.text.secondary,
  },
  '.recharts-reference-line line': { stroke: theme.vars.palette.divider },
  '.recharts-legend-item-text': { color: `${theme.vars.palette.text.primary} !important` },
  // Default Recharts tooltip renders an inline white box; repaint it to the
  // active surface so it matches the custom tooltips (and the rest of the UI) in
  // dark. `!important` is required to beat Recharts' inline background/border.
  '.recharts-default-tooltip': {
    backgroundColor: `${theme.vars.palette.background.paper} !important`,
    border: `1px solid ${theme.vars.palette.divider} !important`,
    borderRadius: theme.shape.borderRadius,
  },
  // Label defaults to (inherited) black, unreadable on a dark surface.
  '.recharts-tooltip-label': { color: theme.vars.palette.text.primary },
});

// Route-aware theme switching. `defaultMode="system"` makes both themes follow
// the OS color-scheme until the user manually picks a mode (persisted by MUI's
// useColorScheme under the shared `mui-mode` key, so the choice survives the
// admin <-> portal theme swap).
const ThemeRouter: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal');
  return (
    <ThemeProvider theme={isPortal ? portalTheme : adminTheme} defaultMode="system">
      <CssBaseline />
      <GlobalStyles styles={rechartsThemeStyles} />
      {children}
    </ThemeProvider>
  );
};

// Fallback while a layout chunk loads: nothing else is on screen yet, so this
// one may fill the viewport (unlike RouteLoadingFallback, which sits inside the
// persistent chrome).
const LayoutLoadingFallback: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
    }}
  >
    <CircularProgress size={48} />
  </Box>
);

// Session expiration handler component - always rendered to catch session expired events
//
// It no longer ends sessions. A signed-in tab lapses instead (`AuthContext`
// owns that decision) and `ReauthDialog` becomes the remedy, so logging out
// here would destroy the page the dialog exists to preserve — and the toast
// would be a second voice telling the user to do what the dialog is already
// asking. What is left is the case with no session to lapse into: a tab that
// was never signed in, or one whose session has already been torn down.
function SessionExpirationHandler() {
  const [sessionExpired, setSessionExpired] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('auth');

  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      console.warn('⚠️ [APP] Authentication error event received', event.detail);
      if (isAuthenticated) return;
      setSessionExpired(true);
    };

    window.addEventListener('session-expired', handleSessionExpired as EventListener);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired as EventListener);
    };
  }, [isAuthenticated]);

  return (
    <Snackbar
      open={sessionExpired}
      autoHideDuration={5000}
      onClose={() => setSessionExpired(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity="warning"
        onClose={() => setSessionExpired(false)}
        sx={{ width: '100%' }}
      >
        {t('session.expired')}
      </Alert>
    </Snackbar>
  );
}

function App() {
  return (
    <HealthProvider>
      <AzureAuthProvider>
        <AuthProvider>
          <Router>
            <ThemeRouter>
              <SessionExpirationHandler />
              <SessionExpiryWarning />
              <ReauthDialog />
              <Routes>
                <Route path="/*" element={
                  <ProtectedRoute>
                    {/* Boundary above the layout chunks: if AppLayout/PortalLayout
                        fail to load even after lazyWithRetry's reload, show a
                        recoverable full-page card instead of a blank tree. */}
                    <RoutedChunkErrorBoundary fullPage>
                    <Suspense fallback={<LayoutLoadingFallback />}>
                      <Routes>
                        {/* A tenant container serves the portal only; the admin app in
                            control-plane mode serves the admin tree only. In full
                            mode (today's admin server) both trees are available. */}
                        <Route path="/portal/*" element={
                          getRuntimeConfig().mode === 'control_plane'
                            ? <Navigate to="/" replace />
                            : (
                              <PortalAccessGuard>
                                <PortalLayout />
                              </PortalAccessGuard>
                            )
                        } />
                        <Route path="/*" element={
                          getRuntimeConfig().mode === 'tenant'
                            ? <Navigate to="/portal" replace />
                            : (
                              <TenantAccessGuard>
                                <AppLayout />
                              </TenantAccessGuard>
                            )
                        } />
                      </Routes>
                    </Suspense>
                    </RoutedChunkErrorBoundary>
                  </ProtectedRoute>
                } />
              </Routes>
            </ThemeRouter>
          </Router>
        </AuthProvider>
      </AzureAuthProvider>
    </HealthProvider>
  );
}

export default App;
